/**
 * One session: who, what, where it is, every verdict with its photo, and the
 * trainer's notes. Polls while the session is live; a finished session offers
 * the PDF report and the certificate.
 *
 * The side rail (notes, timeline) sticks while the verdict list scrolls, so
 * the two halves of the page stay usable on a long run instead of leaving a
 * column of dead space.
 *
 * Mock and real share this component: the mock API client answers the same
 * calls from the demo store.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Play } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { ApiError } from '../api/client';
import type { AuditRow, SessionDetailResponse, SessionStepState, StepRow } from '../api/types';
import {
  Dot,
  EmptyState,
  ErrorState,
  formatDateTime,
  formatDuration,
  formatTime,
  Lightbox,
  Page,
  PageHeader,
  Panel,
  PhotoThumb,
  SessionStatusBadge,
  SkeletonRows,
  StatusBadge,
  timeAgo,
  Toast,
  useToast,
  type Tone,
} from '../components/patterns';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { href } from '../router';

type StepVisual = 'pending' | 'current' | 'verified' | 'retried' | 'failed';

/**
 * The step's state for the strip. The server's per-step state wins when it is
 * present (it knows about retries in flight); the audit log is the fallback.
 */
function stepVisual(
  stepNumber: number,
  current: number,
  serverSteps: SessionStepState[] | undefined,
  audit: AuditRow[],
): StepVisual {
  const s = serverSteps?.find((x) => x.stepNumber === stepNumber);
  if (s) {
    if (s.status === 'verified' || s.status === 'skipped') return 'verified';
    if (s.status === 'failed') return 'failed';
    if (s.status === 'retrying') return 'retried';
    if (stepNumber === current) return 'current';
    return 'pending';
  }
  const forStep = audit.filter((a) => a.stepNumber === stepNumber);
  if (forStep.some((a) => a.eventType === 'verified' && !a.supersededBy)) return 'verified';
  if (forStep.some((a) => a.eventType === 'error')) return 'failed';
  if (forStep.some((a) => a.eventType === 'retry')) return 'retried';
  if (stepNumber === current) return 'current';
  return 'pending';
}

const STEP_CLASS: Record<StepVisual, string> = {
  pending: 'bg-muted text-ink-faint',
  current: 'bg-card text-ink ring-2 ring-field',
  verified: 'bg-verified-soft text-verified',
  retried: 'bg-retry-soft text-retry',
  failed: 'bg-error-soft text-error',
};

export function SessionDetail({ sessionId }: { sessionId: string }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<string | undefined>();
  const [toast, showToast, clearToast] = useToast();

  const sessionQ = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: (q) => (q.state.data?.session.status === 'active' ? 5000 : false),
  });
  const auditQ = useQuery({
    queryKey: ['session', sessionId, 'audit'],
    queryFn: () => api.getSessionAudit(sessionId),
    refetchInterval: sessionQ.data?.session.status === 'active' ? 5000 : false,
  });
  const noteMut = useMutation({
    mutationFn: (text: string) =>
      api.postNote(sessionId, text, sessionQ.data?.state.currentStepNumber),
    onSuccess: () => {
      setNote('');
      showToast('Note saved to the audit log');
      void qc.invalidateQueries({ queryKey: ['session', sessionId, 'audit'] });
    },
    onError: (err) => showToast(err instanceof Error ? err.message : String(err)),
  });
  const reportMut = useMutation({
    mutationFn: () => api.requestReport(sessionId),
    onSuccess: () => showToast('Report requested. It will be ready in a minute.'),
    onError: (err) => showToast(err instanceof Error ? err.message : String(err)),
  });
  const certMut = useMutation({
    mutationFn: () => api.getCertificate(sessionId),
    onSuccess: (cert) => window.open(cert.certUrl, '_blank', 'noopener'),
    onError: (err) =>
      showToast(
        err instanceof ApiError && err.status === 404
          ? 'No certificate yet. It is issued once the report is generated.'
          : err instanceof Error
            ? err.message
            : String(err),
      ),
  });

  if (sessionQ.isLoading) {
    return (
      <Page>
        <Panel flush>
          <SkeletonRows rows={6} />
        </Panel>
      </Page>
    );
  }
  if (sessionQ.error || !sessionQ.data) {
    return (
      <Page>
        <Panel>
          <ErrorState
            error={sessionQ.error ?? 'Session not found'}
            retry={() => void sessionQ.refetch()}
          />
        </Panel>
      </Page>
    );
  }

  const detail: SessionDetailResponse = sessionQ.data;
  const { session } = detail;
  const audit = auditQ.data?.auditLog ?? [];
  const steps = [...detail.steps].sort((a, b) => a.stepNumber - b.stepNumber);
  const current = detail.state.currentStepNumber;
  const verdicts = audit.filter(
    (a) =>
      (a.eventType === 'verified' ||
        a.eventType === 'retry' ||
        a.eventType === 'error' ||
        a.eventType === 'override') &&
      !a.supersededBy,
  );
  const notes = audit.filter((a) => a.eventType === 'note');
  const verifiedCount = steps.filter(
    (s) => stepVisual(s.stepNumber, current, detail.state.steps, audit) === 'verified',
  ).length;
  const technician = session.technicianName ?? 'Technician';
  const finished = session.status !== 'active';

  return (
    <Page>
      <div>
        <a
          href={href({ name: 'sessions' })}
          className="-ml-2 inline-flex items-center gap-1.5 rounded-control px-2 py-1 text-meta font-medium text-ink-dim transition-colors hover:bg-muted hover:text-ink"
        >
          <ArrowLeft className="size-4" /> All sessions
        </a>
      </div>

      <PageHeader
        eyebrow={session.procedureName ?? 'Procedure'}
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {technician}
            <SessionStatusBadge status={session.status} />
          </span>
        }
        lede={
          <>
            {session.equipmentName ?? 'Equipment'}
            {session.equipmentAssetTag ? ` (${session.equipmentAssetTag})` : ''} · v
            {session.procedureVersion} · started {formatDateTime(session.startedAt)}
            {finished && session.durationSeconds != null
              ? ` · ${formatDuration(session.durationSeconds)}`
              : ''}
            {session.testMode ? ' · test run' : ''}
          </>
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <a href={href({ name: 'replay', id: session.id })}>
                <Play /> Replay
              </a>
            </Button>
            {finished ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => certMut.mutate()}
                  disabled={certMut.isPending}
                >
                  Certificate
                </Button>
                <Button onClick={() => reportMut.mutate()} disabled={reportMut.isPending}>
                  <Download /> {reportMut.isPending ? 'Requesting…' : 'PDF report'}
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <Panel
        title={`${verifiedCount} of ${steps.length} steps verified`}
        action={
          session.status === 'active' ? (
            <span className="text-meta text-ink-dim">
              On step {current}: {steps.find((s) => s.stepNumber === current)?.title ?? ''}
            </span>
          ) : null
        }
      >
        <div className="flex flex-wrap gap-1.5">
          {steps.map((s) => {
            const v = stepVisual(s.stepNumber, current, detail.state.steps, audit);
            return (
              <div
                key={s.id}
                title={`Step ${s.stepNumber}: ${s.title}`}
                className={cn(
                  'grid size-9 place-items-center rounded-tile text-meta font-semibold',
                  STEP_CLASS[v],
                )}
              >
                {s.stepNumber}
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-meta text-ink-faint">
          <span className="inline-flex items-center gap-1.5">
            <Dot tone="verified" /> verified
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Dot tone="retry" /> retaken
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Dot tone="error" /> failed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Dot /> not reached
          </span>
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <Panel title="Verdicts" flush bodyClassName="min-h-[16rem]">
          {auditQ.isLoading ? (
            <SkeletonRows rows={4} />
          ) : auditQ.error ? (
            <ErrorState error={auditQ.error} retry={() => void auditQ.refetch()} />
          ) : verdicts.length === 0 ? (
            <EmptyState
              title="No verdicts yet"
              body="Each photo the technician captures is checked and listed here."
            />
          ) : (
            <ul className="divide-y divide-line border-t border-line">
              {[...verdicts].reverse().map((a) => (
                <VerdictRow
                  key={a.id}
                  row={a}
                  step={steps.find((s) => s.stepNumber === a.stepNumber)}
                  onPhoto={setPhoto}
                />
              ))}
            </ul>
          )}
        </Panel>

        <div className="flex flex-col gap-5 lg:sticky lg:top-20">
          <Panel title="Coach notes">
            <textarea
              className="min-h-24 w-full resize-y rounded-control border border-input bg-background px-3 py-2 text-support outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                session.status === 'active'
                  ? `Observation for step ${current}. Saved to the audit log.`
                  : 'Add an observation. Saved to the audit log.'
              }
            />
            <div className="mt-2.5">
              <Button
                size="sm"
                onClick={() => noteMut.mutate(note.trim())}
                disabled={!note.trim() || noteMut.isPending}
              >
                {noteMut.isPending ? 'Saving…' : 'Save note'}
              </Button>
            </div>
            {notes.length > 0 ? (
              <ul className="mt-4 divide-y divide-line border-t border-line">
                {[...notes].reverse().map((n) => (
                  <li key={n.id} className="py-2.5">
                    <div className="text-support text-ink">{n.detail ?? n.message}</div>
                    <div className="mt-0.5 text-meta text-ink-faint">
                      {n.stepNumber ? `Step ${n.stepNumber} · ` : ''}
                      {timeAgo(n.timestamp)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </Panel>

          <Panel title="Timeline" flush bodyClassName="max-h-[28rem] overflow-y-auto">
            {audit.length === 0 ? (
              <EmptyState title="Nothing recorded yet" />
            ) : (
              <ol className="relative px-4 pb-4 sm:px-5">
                {/* The connector: one line down the rail, so the entries read
                    as a sequence rather than as loose dots. */}
                <span
                  className="absolute top-2 bottom-4 left-[21px] w-px bg-line sm:left-[25px]"
                  aria-hidden
                />
                {[...audit]
                  .reverse()
                  .slice(0, 40)
                  .map((a) => (
                    <li key={a.id} className="relative flex items-start gap-3 py-1.5 pl-0">
                      <span className="relative z-10 mt-1 grid size-3.5 shrink-0 place-items-center rounded-full bg-card">
                        <Dot tone={toneFor(a)} className="size-2" />
                      </span>
                      <span className="min-w-0 flex-1 text-meta text-ink">{describeEvent(a)}</span>
                      <span className="shrink-0 text-meta text-ink-faint">
                        {formatTime(a.timestamp)}
                      </span>
                    </li>
                  ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>

      <Lightbox src={photo} onClose={() => setPhoto(undefined)} />
      <Toast message={toast} onDone={clearToast} />
    </Page>
  );
}

function VerdictRow({
  row,
  step,
  onPhoto,
}: {
  row: AuditRow;
  step?: StepRow;
  onPhoto: (src: string) => void;
}) {
  const tone: Tone =
    row.eventType === 'verified' || (row.eventType === 'override' && row.verified)
      ? 'verified'
      : row.eventType === 'error'
        ? 'error'
        : 'retry';
  const label =
    row.eventType === 'verified'
      ? 'Verified'
      : row.eventType === 'retry'
        ? 'Retake'
        : row.eventType === 'override'
          ? row.verified
            ? 'Override: pass'
            : 'Override: fail'
          : 'Error';
  return (
    <li className="flex gap-4 px-4 py-4 sm:px-5">
      {row.photoUrl ? (
        <PhotoThumb
          src={row.photoUrl}
          alt={`Step ${row.stepNumber ?? ''} photo`}
          onOpen={onPhoto}
          className="size-20 sm:size-24"
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-support font-semibold text-ink">
            Step {row.stepNumber}
            {step ? `: ${step.title}` : ''}
          </div>
          <div className="flex items-center gap-2">
            {row.confidence ? (
              <span className="text-meta text-ink-faint">{row.confidence} confidence</span>
            ) : null}
            <StatusBadge tone={tone}>{label}</StatusBadge>
          </div>
        </div>
        {row.message ? <p className="mt-1 text-support text-ink">{row.message}</p> : null}
        {row.detail ? <p className="mt-1 text-meta text-ink-dim">{row.detail}</p> : null}
        <div className="mt-1.5 text-meta text-ink-faint">{formatTime(row.timestamp)}</div>
      </div>
    </li>
  );
}

function toneFor(a: AuditRow): Tone {
  switch (a.eventType) {
    case 'verified':
    case 'complete':
      return 'verified';
    case 'retry':
    case 'abandon':
      return 'retry';
    case 'error':
      return 'error';
    case 'photo_submitted':
    case 'start':
      return 'field';
    default:
      return 'neutral';
  }
}

function describeEvent(a: AuditRow): string {
  const step = a.stepNumber ? `Step ${a.stepNumber}` : 'Session';
  switch (a.eventType) {
    case 'start':
      return 'Session started';
    case 'complete':
      return 'Session completed';
    case 'abandon':
      return `Session abandoned${a.detail ? `: ${a.detail}` : ''}`;
    case 'photo_submitted':
      return `${step}: photo submitted`;
    case 'verified':
      return `${step}: verified`;
    case 'retry':
      return `${step}: retake requested`;
    case 'skip':
      return `${step}: skipped`;
    case 'error':
      return `${step}: verification error`;
    case 'override':
      return `${step}: supervisor override (${a.verified ? 'pass' : 'fail'})`;
    case 'note':
      return `Note: ${a.detail ?? ''}`;
    default:
      return `${step}: ${a.eventType}`;
  }
}
